import "./style.scss";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { TabContext, TabPanel } from "@mui/lab";
import { CircularProgress, Drawer, Tooltip } from "@mui/material";
import CollectiveManagementInfoModal from "./CollectiveManagementInfoModal";
import ActionSend from "assets/img/svg/actionSend";
import Downloadnew from "assets/img/svg/downloadnew";
import SendEmailIcon from "assets/img/svg/sendEmail";
import ButtonBase from "components/shared/ButtonBase";
import EmptyState from "components/shared/EmptyState";
import SendEmailBeneficiar from "components/shared/SendEmailBeneficiar";
import CustomStatementBlock from "components/shared/ShowStatementsPublisher/CustomStatementBlock";
import { Spinner } from "components/shared/Spinner";
import { WhiteDropdownMenu } from "components/shared/WhiteDropdownMenu";
import { getStatementFilename } from "helpers/utils";
import { convertAnyAsArray } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import type { ITermBeneficiar } from "store/managementStore";
import { fetchPartnerStatements } from "store/partnerStatementsStore";
import { fetchCompanyInfo } from "store/settingsStore";
import {
    fetchAndSaveBeneficiarStatement,
    fetchBeneficiaryNetStatement,
    fetchBeneficiaryPrecountStatement,
    fetchBeneficiaryStatement,
    sendBeneficiaryMergedStatements,
    setStatementByBeneficiary,
} from "store/statementsStore";
import type { AppDispatch, RootState } from "store/store";
import { useAppSelector } from "store/store";

import { PeriodStatus } from "store/types";
import variables from "../../../../assets/styles/_variables.module.scss";

type TProps = {
    currentBeneficiary: ITermBeneficiar | null;
    setIsOpen: React.Dispatch<React.SetStateAction<string | null>>;
    isOpen: boolean;
    statementCurrency: string;
};

const statementType = [
    { labelKey: "Beneficiars.Statement", type: "gross" },
    { labelKey: "Summary.StatementOfNetDuties", type: "net" },
    { labelKey: "Summary.PrepaymentCertification", type: "precount" },
];

const BeneficiaryStatement = ({ setIsOpen, isOpen, currentBeneficiary, statementCurrency }: TProps) => {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { getCompanySettings, companyInfos, countryFeatures } = useCompanySettings();
    const usesStatementTemplates = getCompanySettings("uses_custom_statement_templates");
    const { showNet, showPrecount } = countryFeatures.statementBlocks;
    const period = useSelector((state: RootState) => state.period.activePeriod);
    const periodStatus = useSelector((state: RootState) => state.period.activePeriodData?.isActive) as
        | PeriodStatus
        | undefined;
    const isPeriodFuture = periodStatus === PeriodStatus.FUTURE;
    const partnerStatements = useAppSelector((state) => state.partnerStatements.partnerStatements);
    const { statementByBeneficiary, netStatementByBeneficiary, precountStatementByBeneficiary, status, savingStatus } =
        useSelector((state: RootState) => state.statements);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [showCustomStatement, setShowCustomStatement] = useState(false);
    const hasAuthorPortalAccess = currentBeneficiary?.accountStatus && currentBeneficiary?.accountStatus == "confirmed";
    const sendTermText = hasAuthorPortalAccess ? t("Summary.SendToBeneficiarPortal") : t("Summary.SendToBeneficiar");
    const isCustomStatement = !!currentBeneficiary?.customStatementTemplateId && currentTab == 0;
    const isPrecounted =
        currentBeneficiary?.precount == "1" ||
        (currentBeneficiary?.precount == "2" && (currentBeneficiary?.contributions ?? 0) > 0);
    const beneficiaryCustomStatement = useMemo(
        () => partnerStatements.find((statement) => statement.id == currentBeneficiary?.customStatementTemplateId),
        [currentBeneficiary, partnerStatements],
    );
    const isStatementCurrencyDefault = companyInfos?.currency === statementCurrency;
    const hasCollectiveManagement =
        (currentBeneficiary?.hasPostCloseCollectiveManagementSales ?? 0) > 0 && periodStatus === PeriodStatus.CLOSED;
    const drawerPaperRef = useRef<HTMLDivElement | null>(null);
    const [drawerLeft, setDrawerLeft] = useState<number>(0);
    const [showCMInfoModal, setShowCMInfoModal] = useState(false);

    useEffect(() => {
        if (isOpen && hasCollectiveManagement) {
            setShowCMInfoModal(false);
            const timer = setTimeout(() => {
                if (drawerPaperRef.current) {
                    setDrawerLeft(drawerPaperRef.current.getBoundingClientRect().left);
                }
                setShowCMInfoModal(true);
            }, 300);
            return () => clearTimeout(timer);
        } else {
            setShowCMInfoModal(false);
        }
    }, [isOpen, currentBeneficiary?.id]);

    const disableReason = useMemo(() => {
        if (!isStatementCurrencyDefault) return "default-currency";
        else return "";
    }, [isStatementCurrencyDefault]);

    const toggleDrawer =
        (anchor: any, open: any): any =>
        (event: any) => {
            if (
                event &&
                event.type === "keydown" &&
                ((event as React.KeyboardEvent).key === "Tab" || (event as React.KeyboardEvent).key === "Shift")
            ) {
                return;
            }
            open ? setIsOpen("statement") : setIsOpen("");
        };

    useEffect(() => {
        if (isCustomStatement && !partnerStatements.length) dispatch(fetchPartnerStatements());
    }, [isCustomStatement]);

    const isMergedStatementsEnabled = useMemo(() => {
        return getCompanySettings("beneficiary_merged_statements_enabled");
    }, [getCompanySettings]);

    const isMergedStatementsGenerating = useMemo(() => {
        return getCompanySettings("beneficiary_merged_statements_requested");
    }, [getCompanySettings]);

    /**
     * Reset initial statementByBeneficiary when changing beneficiary;
     */
    useEffect(() => {
        if (usesStatementTemplates) {
            dispatch(setStatementByBeneficiary(""));
            setShowCustomStatement(false);
        }
    }, [currentBeneficiary]);

    useEffect(() => {
        if (!isPrecounted && currentTab == 2) setCurrentTab(0);
        if (period && currentBeneficiary) {
            currentTab == 0 &&
                !isCustomStatement &&
                dispatch(
                    fetchBeneficiaryStatement({
                        beneficiaryId: currentBeneficiary.id,
                        periodId: period,
                    }),
                );
            currentTab == 0 &&
                isCustomStatement &&
                showCustomStatement &&
                !statementByBeneficiary &&
                dispatch(
                    fetchBeneficiaryStatement({
                        beneficiaryId: currentBeneficiary.id,
                        periodId: period,
                    }),
                );
            currentTab == 1 &&
                dispatch(
                    fetchBeneficiaryNetStatement({
                        beneficiaryId: currentBeneficiary.id,
                        periodId: period,
                    }),
                );
            currentTab == 2 &&
                dispatch(
                    fetchBeneficiaryPrecountStatement({
                        beneficiaryId: currentBeneficiary.id,
                        periodId: period,
                    }),
                );
        }
    }, [period, currentBeneficiary, currentTab, dispatch, showCustomStatement, isCustomStatement]);

    const savePdf = useCallback(() => {
        if (savingStatus == "pending") return;
        if (period) {
            dispatch(
                fetchAndSaveBeneficiarStatement({
                    beneficiarId: currentBeneficiary!.id,
                    periodId: period,
                    fileName: getStatementFilename(
                        `${currentBeneficiary?.surname ?? ""} ${currentBeneficiary?.name ?? ""}`,
                        "",
                        t(statementType[currentTab].labelKey),
                        new Date(),
                    ),
                    statementType: statementType[currentTab].type,
                }),
            );
        }
    }, [period, dispatch, currentBeneficiary, currentTab, savingStatus]);

    const downloadMergedStatements = useCallback(
        (grossStatementsOnly?: boolean) => {
            if (savingStatus == "pending") return;
            if (period) {
                dispatch(
                    sendBeneficiaryMergedStatements({
                        beneficiaryId: currentBeneficiary!.id,
                        periodId: period,
                        grossStatementsOnly,
                    }),
                );
            }
        },
        [currentBeneficiary, dispatch, period, savingStatus],
    );

    return (
        <>
            <CollectiveManagementInfoModal
                isOpen={hasCollectiveManagement && showCMInfoModal}
                drawerLeft={drawerLeft}
                onClose={() => setShowCMInfoModal(false)}
            />
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={toggleDrawer("right", false)}
                PaperProps={{ ref: drawerPaperRef }}
            >
                <SendEmailBeneficiar
                    isModalOpen={isModalOpen}
                    hasCustomStatementsBeneficiars={[]}
                    beneficiaries={convertAnyAsArray(currentBeneficiary)}
                    periodId={period}
                    onClose={() => setIsModalOpen(false)}
                    isBulk={false}
                />
                <div className="show-statements-beneficiars">
                    <div className="summary-show-statements-header">
                        <div className="summary-show-statements-goBack" onClick={() => setIsOpen("")}>
                            <KeyboardArrowLeftIcon />
                            {t("ShowStatements.GoBack")}
                        </div>
                        <div className="summary-show-statements-current-statement">{`${currentBeneficiary?.surname?.toUpperCase()} ${currentBeneficiary?.name}`}</div>
                        <div className="summary-show-statements-header-actions">
                            {isMergedStatementsEnabled ? (
                                <div className="w-[120px] flex justify-end items-center">
                                    {(savingStatus == "pending" && (
                                        <CircularProgress size={20} sx={{ color: variables["primary-purple"] }} />
                                    )) || (
                                        <WhiteDropdownMenu
                                            options={[
                                                {
                                                    id: 1,
                                                    name: t("ShowStatements.DownloadCombined"),
                                                    disabled: isMergedStatementsGenerating,
                                                    action: () => {
                                                        downloadMergedStatements();
                                                    },
                                                },
                                                {
                                                    id: 2,
                                                    name: t("ShowStatements.DownloadCombinedGrossOnly"),
                                                    disabled: isMergedStatementsGenerating,
                                                    action: () => {
                                                        downloadMergedStatements(true);
                                                    },
                                                },
                                            ]}
                                            style={{ backgroundColor: "white" }}
                                            buttonName={t("ShowStatements.Download")}
                                            onMenuClick={() => {
                                                // refresh settings
                                                dispatch(fetchCompanyInfo());
                                            }}
                                            mainAction={() => {
                                                savePdf();
                                            }}
                                        />
                                    )}
                                </div>
                            ) : (
                                <>
                                    {isCustomStatement ? (
                                        <></>
                                    ) : (
                                        <div className="summary-show-statements-header-button" onClick={savePdf}>
                                            {(savingStatus == "pending" && (
                                                <CircularProgress
                                                    size={20}
                                                    sx={{ color: variables["primary-purple"] }}
                                                />
                                            )) || <Downloadnew />}
                                        </div>
                                    )}
                                </>
                            )}
                            {!isPeriodFuture && (
                                <Tooltip
                                    title={t("Summary.NotAvailableCustomStatement")}
                                    disableHoverListener={!isCustomStatement}
                                >
                                    <div className="summary-show-statements-header-buttonBase">
                                        <ButtonBase
                                            styles={{ borderRadius: "5px" }}
                                            variant="contained"
                                            disabled={
                                                !currentBeneficiary!.email ||
                                                (!!hasAuthorPortalAccess && !!currentBeneficiary.lastSentAt) ||
                                                isCustomStatement
                                            }
                                            onClick={() => setIsModalOpen(true)}
                                        >
                                            {currentBeneficiary?.email
                                                ? hasAuthorPortalAccess && currentBeneficiary?.lastSentAt
                                                    ? t("SendEmail.AlreadyTransmit")
                                                    : sendTermText
                                                : t("Summary.NoEmail")}
                                            {hasAuthorPortalAccess ? <ActionSend /> : <SendEmailIcon />}
                                        </ButtonBase>
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>
                    <div className="summary-statements-buttons-list">
                        <div
                            className={`summary-statements-button${(currentTab == 0 && " active") || ""}`}
                            onClick={() => setCurrentTab(0)}
                        >
                            {t("Summary.StatementOfGrossDuties")}
                        </div>
                        {showNet && (
                            <Tooltip
                                title={t("Summary.NoNet", { companyCurrency: companyInfos?.currency })}
                                disableHoverListener={!disableReason}
                            >
                                <div
                                    className={`summary-statements-button${(currentTab == 1 && " active") || ""} ${
                                        disableReason ? "disabled" : ""
                                    }`}
                                    onClick={() => !disableReason && setCurrentTab(1)}
                                >
                                    {t("Summary.StatementOfNetDuties")}
                                </div>
                            </Tooltip>
                        )}

                        {showPrecount && (
                            <Tooltip
                                title={t("Summary.NoNet", { companyCurrency: companyInfos?.currency })}
                                disableHoverListener={!disableReason}
                            >
                                <div
                                    className={`summary-statements-button${(currentTab == 2 && " active") || ""}${
                                        !isPrecounted || disableReason ? " disabled" : ""
                                    }`}
                                    onClick={() => isPrecounted && !disableReason && setCurrentTab(2)}
                                >
                                    {t("Summary.PrepaymentCertification")}
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    <div className={`show-statements-main ${isCustomStatement ? "custom-statement" : ""}`}>
                        <TabContext value={currentTab.toString()}>
                            <TabPanel sx={{ padding: 0 }} value="0">
                                {isCustomStatement && (
                                    <CustomStatementBlock
                                        showCustomStatement={showCustomStatement}
                                        statementByBeneficiary={statementByBeneficiary}
                                        beneficiaryCustomStatement={beneficiaryCustomStatement}
                                        status={status}
                                        onShowCustomStatement={() => setShowCustomStatement(true)}
                                    />
                                )}
                                {!isCustomStatement && (
                                    <>
                                        {status === "pending" && (
                                            <div className="spinner-wrapper">
                                                <Spinner />
                                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                            </div>
                                        )}
                                        {status === "failed" && (
                                            <div className="spinner-wrapper">
                                                <EmptyState type="statementFailed" />
                                            </div>
                                        )}
                                        {status === "succeeded" && (
                                            <div
                                                style={{
                                                    maxWidth: "960px",
                                                    border: "39px solid #c8c8c852",
                                                    paddingBottom: 24,
                                                    minHeight: 1000,
                                                }}
                                                dangerouslySetInnerHTML={{ __html: statementByBeneficiary }}
                                            ></div>
                                        )}
                                        {status === "idle" && (
                                            <div style={{ textAlign: "center" }}>some strange comment</div>
                                        )}
                                    </>
                                )}
                            </TabPanel>
                            {showNet && (
                                <TabPanel sx={{ padding: 0 }} value="1">
                                    {status === "pending" && (
                                        <div className="spinner-wrapper">
                                            <Spinner />
                                            <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                        </div>
                                    )}
                                    {status === "failed" && (
                                        <div className="spinner-wrapper">
                                            <EmptyState type="statementFailed" />
                                        </div>
                                    )}
                                    {status === "succeeded" && (
                                        <div
                                            style={{
                                                maxWidth: "960px",
                                                border: "39px solid #c8c8c852",
                                                paddingBottom: 24,
                                                minHeight: 1000,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: netStatementByBeneficiary,
                                            }}
                                        ></div>
                                    )}
                                    {status === "idle" && (
                                        <div style={{ textAlign: "center" }}>some strange comment</div>
                                    )}
                                </TabPanel>
                            )}
                            {showPrecount && (
                                <TabPanel sx={{ padding: 0 }} value="2">
                                    {status === "pending" && (
                                        <div className="spinner-wrapper">
                                            <Spinner />
                                            <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                                        </div>
                                    )}
                                    {status === "failed" && (
                                        <div className="spinner-wrapper">
                                            <EmptyState type="statementFailed" />
                                        </div>
                                    )}
                                    {status === "succeeded" && (
                                        <div
                                            style={{
                                                maxWidth: "960px",
                                                border: "39px solid #c8c8c852",
                                                paddingBottom: 24,
                                                minHeight: 1000,
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: precountStatementByBeneficiary,
                                            }}
                                        ></div>
                                    )}
                                    {status === "idle" && (
                                        <div style={{ textAlign: "center" }}>some strange comment</div>
                                    )}
                                </TabPanel>
                            )}
                        </TabContext>
                    </div>
                </div>
            </Drawer>
        </>
    );
};
export default BeneficiaryStatement;
